import {Trans} from '@lingui/macro';
import React, {Component} from 'react';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {
  AsyncHandlerOf,
  EventHandler,
  HandlerOf,
} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CustomError} from '../../../../../../libs/shared/helpers/src/types/customErrors';
import {ToolbarButton} from '../../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {SearchTagModel} from '../../../../../models/api/searchTagModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {TeamModel} from '../../../../../models/api/teamModel';
import {ResourceMap} from '../../../../../store/appTypes';
import {DropdownFetcher} from '../../../../common/dropdowns/source/asyncDropdownSource';
import {CreateTagDropdownContainer} from '../../../../common/dropdowns/tags/createTagDropdownContainer';
import {TagTemplate} from '../../../../common/dropdowns/tags/tagEditorBody';
import {ComposerTagsDropdown} from '../dropdowns/composerTagsDropdown';

/*
 * Props.
 */

export interface TagsToolbarButtonProps {
  createTag: AsyncHandlerOf<TagTemplate>;
  teams: ResourceMap<TeamModel>;
  userTeams: ReadonlyArray<TeamModel>;
  namespace: string;
  fetchTagsAsync: DropdownFetcher<SearchTagModel | null>;
  tagMessage: HandlerOf<SearchTagModel>;
  untagMessage: HandlerOf<TagModel>;
  selectedTagIds: ReadonlyArray<number>;
}

/*
 * Component.
 */

interface TagsToolbarButtonState {
  isCreateOpen: boolean;
  isSaving: boolean;
  tagError: CustomError | undefined;
}

export class TagsToolbarButton extends Component<TagsToolbarButtonProps, TagsToolbarButtonState> {
  constructor(props: TagsToolbarButtonProps) {
    super(props);
    this.state = {
      isCreateOpen: false,
      isSaving: false,
      tagError: undefined,
    };
  }

  /*
   * Event handlers.
   */

  private readonly toggleIsCreateOpen = () => {
    this.setState(({isCreateOpen}) => ({isCreateOpen: !isCreateOpen}));
  };

  /*
   * Render.
   */

  render() {
    return (
      <PopoverCoordinator
        renderButton={(isOpen) => renderButton(isOpen, this.props)}
        renderModal={this.renderDropdown}
        keyboardAction={KeyboardActionsEnum.TAG}
      />
    );
  }

  /*
   * Dropdown.
   */

  private readonly renderDropdown = (popoverProps: PopoverTransferProps) => {
    if (this.state.isCreateOpen) {
      return this.renderCreateDropdown(popoverProps);
    }

    return this.renderAddDropdown(popoverProps);
  };

  /*
   * Add tag dropdown.
   */

  private renderAddDropdown(popoverProps: PopoverTransferProps) {
    const onCreateTagSelect: EventHandler = (e) => {
      e.preventDefault(); // Keep the dropdown open
      this.toggleIsCreateOpen(); // Switch to Create Tag
    };

    return <ComposerTagsDropdown {...this.props} {...popoverProps} onCreateTagSelect={onCreateTagSelect} />;
  }

  /*
   * Create tag dropdown.
   */

  private renderCreateDropdown(popoverProps: PopoverTransferProps) {
    const onRequestClose = () => {
      popoverProps.onRequestClose(); // Close the dropdown
      this.toggleIsCreateOpen(); // Switch back to Add Tag
    };

    const onSaveTag = async (tag: TagTemplate) => {
      this.setState({tagError: undefined, isSaving: true});

      try {
        await this.props.createTag(tag);

        onRequestClose();
      } catch (error) {
        if (!(error instanceof CustomError)) {
          throw error;
        }

        // In case of conflict with an existing tag, display an error.
        this.setState({tagError: error});
      } finally {
        this.setState({isSaving: false});
      }
    };

    return (
      <CreateTagDropdownContainer
        {...this.props}
        {...popoverProps}
        resourceNamespace={this.props.namespace}
        title={<Trans id="tag-create-title">Create new tag</Trans>}
        okButtonLabel={<Trans id="tag-create-ok">Create</Trans>}
        onRequestClose={onRequestClose}
        onOkClick={onSaveTag}
        isSaving={this.state.isSaving}
        tagError={this.state.tagError}
      />
    );
  }
}

/*
 * Renders.
 */

function renderButton(isOpen: boolean, props: TagsToolbarButtonProps) {
  const iconName = props.selectedTagIds.length > 0 ? 'tagFilled' : 'tag';
  return (
    <UserThemeConsumer>
      {(theme) => (
        <ToolbarButton
          iconName={iconName}
          color={props.selectedTagIds.length > 0 ? theme.palette.blue.shade40 : theme.greys.shade80}
          hoverColor={theme.greys.shade80}
          hasHover={isOpen}
          label={<Trans id="conversation-tags">Add tags</Trans>}
          keyboardAction={KeyboardActionsEnum.TAG}
        />
      )}
    </UserThemeConsumer>
  );
}
